import React, { useEffect, useState } from 'react';
import { findItemIn, getTrendItemScopedTo, getTrendStores } from '@/trend/trendDataHelper.utilities';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useStateWithRef } from '@/core/hooks/useStateWithRef.hook';
import { MAX_NAME_LENGTH } from '@/main/app.constants';
import { PREVIEW_ID } from '@/trendData/trendData.constants';
import { ColorPicker } from '@/workbook/ColorPicker.organism';
import { FormError } from '@/core/FormError.atom';
import { useIsMounted } from '@/core/hooks/useIsMounted.hook';
import { setTrendItemColor } from '@/trendData/trend.actions';
import { getDefaultName } from '@/utilities/formula.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { Icon, InputGroup } from '@seeqdev/qomponents';
import { useDidMount } from 'rooks';

interface SearchTitleProps {
  name: string;
  id: string;
  setSearchName: (name: string) => void;
  searchIconClass: string;
  defaultName: string;
  onColorChange?: (color: string) => void;
  color?: string;
  fromFormBuilder?: boolean;
  className?: any;
  types?: string[];
}

const SearchTitleUnwrapped: React.FunctionComponent<SearchTitleProps> = (props) => {
  const { id, setSearchName, searchIconClass, defaultName, color, onColorChange, fromFormBuilder, types } = props;
  const { t } = useTranslation();
  const [name, setName] = useStateWithRef(props.name ? props.name : '');
  const [loadingDefaultName, setLoadingDefaultName] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isMounted = useIsMounted();

  useEffect(() => {
    setName(props.name);
    // exhaustive deps does not work well with setters from custom hooks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.name]);

  useDidMount(() => {
    if (_.isEmpty(name.current)) {
      setLoadingDefaultName(true);
      getDefaultName(t(defaultName), getTrendItemScopedTo(id), [SeeqNames.API.Flags.ExcludeGloballyScoped], types).then(
        (defaultName) => {
          if (isMounted.current) {
            setLoadingDefaultName(false);
            if (_.isEmpty(name.current)) {
              setName(defaultName);
              setSearchName(defaultName);
            }
          }
        },
      );
    }

    if (!onColorChange) {
      return;
    }

    const currentId = id || PREVIEW_ID;
    const trendStores = getTrendStores();
    const existingItem: any = findItemIn(trendStores, currentId);
    const nextColor = trendStores?.[0].findNextColor();
    const itemColor = existingItem?.color ?? nextColor;

    if (color !== itemColor) {
      onColorChange(itemColor);
    }
  });

  const updateName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setName(name);
    setSearchName(name);
  };

  return (
    <div>
      <h4 className="card-title searchTitleDiv" style={{ display: 'block', width: '100%' }}>
        {isEditing ? (
          <div className="searchTitleInput">
            <InputGroup
              id="name"
              value={name.current ? name.current : ''}
              showError={(name.current?.length || 0) > MAX_NAME_LENGTH.TOOL}
              maxLength={MAX_NAME_LENGTH.TOOL}
              onChange={updateName}
              onFocus={(e: any) => e.target.select()}
              onBlur={() => setIsEditing(false)}
              testId="searchTitleInput"
              required={true}
              autoFocus={true}
              append={[]}
            />
          </div>
        ) : (
          <div
            className="displayFlex flexAlignStart width100"
            data-testid="searchTitleDisplay"
            style={{ lineHeight: '1' }}>
            <span
              className="flexFill pr5 cursorPointer"
              onClick={() => setIsEditing(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                  e.preventDefault();
                  setIsEditing(true);
                }
              }}
              tabIndex={0}
              role="button"
              style={{ lineHeight: '1', paddingTop: '0' }}>
              {name.current || t(defaultName)}
            </span>
            <div className="displayFlex flexAlignCenter flexNoShrink" style={{ marginLeft: 'auto' }}>
              <span
                onClick={() => setIsEditing(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    setIsEditing(true);
                  }
                }}
                tabIndex={0}
                role="button"
                className="cursorPointer displayFlex flexAlignCenter"
                style={{ padding: '4px', fontSize: '14px' }}>
                <Icon icon="fa-pencil" type="color" color="#999" small={true} />
              </span>
              {onColorChange && (
                <div style={{ padding: '4px' }}>
                  <ColorPicker
                    color={color}
                    itemId={id}
                    placement="bottom"
                    notifyOnSelect={(_, newColor) => {
                      onColorChange(newColor);
                      setTrendItemColor(id, newColor);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </h4>
      <hr className="searchTitleDivider" />

      {!fromFormBuilder && _.isEmpty(_.trim(name.current)) && !loadingDefaultName && (
        <FormError extraClassNames="ml25" errorText="FORM.REQUIRED_FIELD" />
      )}

      {!fromFormBuilder && name.current?.length > MAX_NAME_LENGTH.TOOL && (
        <FormError
          extraClassNames="ml25"
          errorText="NAME_LENGTH_ERROR"
          errorParameters={{ count: MAX_NAME_LENGTH.TOOL }}
        />
      )}
    </div>
  );
};

export const SearchTitle = React.memo(
  SearchTitleUnwrapped,
  (prev, next) =>
    !(
      prev.searchIconClass !== next.searchIconClass ||
      prev.className !== next.className ||
      prev.name !== next.name ||
      prev.color !== next.color
    ),
);
